import { useCallback } from 'react';
import { RecentlyViewedData, RecentlyViewedResponse } from '@atlassian/cssxp-typescommon';

interface RecentlyViewedConfig {
  appName: string;
  maxPerAppCount?: number;
  maxPerEntityCount?: number;
  trackTitle?: boolean;
  storagePrefix?: string;
}

interface UpdateRecentlyViewedData {
  url?: string;
  pageTitle?: string;
  entityName?: string;
  metadata?: any;
  key?: string;
}

let appName: string, maxPerAppCount: number, maxPerEntityCount: number, trackTitle: boolean, storagePrefix: string;

const recentlyViewedKey = 'recently_viewed_pages_';
export const useRecentlyViewed = () => {
  const init = useCallback((config: RecentlyViewedConfig) => {
    appName = config.appName;
    maxPerEntityCount = config.maxPerEntityCount || 20;
    maxPerAppCount = config.maxPerAppCount || 50;
    trackTitle = config.trackTitle || false;
    storagePrefix = config.storagePrefix ? config.storagePrefix + '_' : '';
  }, []);

  const updateRecentlyViewed = ({ url, pageTitle, entityName, metadata, key }: UpdateRecentlyViewedData) => {
    if (!url) {
      url = location.pathname;
    }

    // storing pages at app level
    let parsedAppData: RecentlyViewedData[] = [];
    try {
      const appData = key
        ? localStorage.getItem(`${key}${appName}`)
        : localStorage.getItem(`${storagePrefix}${recentlyViewedKey}${appName}`);
      parsedAppData = appData ? JSON.parse(appData) : [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching recently viewed pages:', error);
    }

    const updatedRecentlyViewedAppData = [...parsedAppData];
    // remove trailing / from url
    url = url.replace(/\/$/, '');
    const foundIndexOfAppData = updatedRecentlyViewedAppData.findIndex((page) => page.url === url);
    if (foundIndexOfAppData !== -1) {
      updatedRecentlyViewedAppData[foundIndexOfAppData].timestamp = new Date().getTime();
      updatedRecentlyViewedAppData[foundIndexOfAppData].pageTitle = pageTitle;
    } else {
      if (updatedRecentlyViewedAppData.length === maxPerAppCount) {
        updatedRecentlyViewedAppData.sort((a, b) => b.timestamp - a.timestamp);
        updatedRecentlyViewedAppData.pop();
      }
      updatedRecentlyViewedAppData.unshift({
        url,
        pageTitle,
        timestamp: new Date().getTime(),
      });
    }

    // storing pages at entity level
    if (entityName) {
      let parsedEntityData: RecentlyViewedData[] = [];
      try {
        const entityData = key
          ? localStorage.getItem(`${key}${appName}_${entityName}`)
          : localStorage.getItem(`${storagePrefix}${recentlyViewedKey}${appName}_${entityName}`);
        parsedEntityData = entityData ? JSON.parse(entityData) : [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching recently viewed pages:', error);
      }
      const updatedRecentlyViewedEntityData = [...parsedEntityData];
      const foundIndexOfEntityData = updatedRecentlyViewedEntityData.findIndex((page) => page.url === url);
      if (foundIndexOfEntityData !== -1) {
        updatedRecentlyViewedEntityData[foundIndexOfEntityData].timestamp = new Date().getTime();
        updatedRecentlyViewedEntityData[foundIndexOfEntityData].entityName = entityName;
        updatedRecentlyViewedEntityData[foundIndexOfEntityData].metadata = metadata;
        updatedRecentlyViewedEntityData[foundIndexOfEntityData].pageTitle = pageTitle;
      } else {
        if (updatedRecentlyViewedEntityData.length === maxPerEntityCount) {
          updatedRecentlyViewedEntityData.sort((a, b) => b.timestamp - a.timestamp);
          updatedRecentlyViewedEntityData.pop();
        }
        updatedRecentlyViewedEntityData.unshift({
          url,
          timestamp: new Date().getTime(),
          entityName,
          pageTitle,
          metadata,
        });
      }
      try {
        key
          ? localStorage.setItem(`${key}${appName}_${entityName}`, JSON.stringify(updatedRecentlyViewedEntityData))
          : localStorage.setItem(
              `${storagePrefix}${recentlyViewedKey}${appName}_${entityName}`,
              JSON.stringify(updatedRecentlyViewedEntityData)
            );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating recently viewed pages:', error);
      }

      // update the app storage data as well
      updatedRecentlyViewedAppData[0].metadata = metadata;
      updatedRecentlyViewedAppData[0].entityName = entityName;
    }

    try {
      key
        ? localStorage.setItem(`${key}${appName}`, JSON.stringify(updatedRecentlyViewedAppData))
        : localStorage.setItem(
            `${storagePrefix}${recentlyViewedKey}${appName}`,
            JSON.stringify(updatedRecentlyViewedAppData)
          );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error setting recently viewed pages:', error);
    }
  };

  const getRecentlyViewed = <T>(entityName?: string): RecentlyViewedResponse<T> => {
    let hasErrorGettingAppData = false;
    let hasErrorGettingEntityData = false;
    if (entityName) {
      let parsedEntityData: RecentlyViewedData<T>[] = [];
      try {
        const entityData = localStorage.getItem(`${storagePrefix}${recentlyViewedKey}${appName}_${entityName}`);
        parsedEntityData = entityData ? JSON.parse(entityData) : [];
      } catch (error) {
        hasErrorGettingEntityData = true;
      }
      parsedEntityData.sort((a, b) => b.timestamp - a.timestamp);
      return { recentlyVisitedData: parsedEntityData, hasError: hasErrorGettingEntityData };
    }
    let parsedAppData: RecentlyViewedData<T>[] = [];
    try {
      const appData = localStorage.getItem(`${storagePrefix}${recentlyViewedKey}${appName}`);
      parsedAppData = appData ? JSON.parse(appData) : [];
    } catch (error) {
      hasErrorGettingAppData = true;
    }
    parsedAppData.sort((a, b) => b.timestamp - a.timestamp);
    return { recentlyVisitedData: parsedAppData, hasError: hasErrorGettingAppData };
  };

  return { updateRecentlyViewed, init, getRecentlyViewed };
};
